import React, { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { createTargetGroup, ensureTargetGroupLinked, errorMessage, getProject, getProjectReadiness } from '../../api';
import { paths } from '../../app/paths';
import { useAsyncData } from '../../hooks/useAsyncData';
import { formatDate } from '../../lib/format';
import type { Project, ProjectReadiness, TargetGroup } from '../../types';
import { Badge, Button, Card, CardBody, EmptyState, ErrorState, Skeleton, StatusBadge, Table, Tabs, Td, Th, useToast } from '../../ui';
import { CampaignDetailsDialog } from './CampaignDetailsDialog';
import { CampaignStatusControl } from './CampaignStatusControl';
import { CampaignProgressCard } from './CampaignProgressCard';
import { RespondentsPanel } from '../respondents/RespondentsPanel';
import { EntryLinkCard } from './EntryLinkCard';
import { LaunchChecklist } from './LaunchChecklist';
import { PricingCard } from './PricingCard';
import { RedirectLinksCard } from './RedirectLinksCard';
import { SaveAsTemplateDialog } from '../templates/SaveAsTemplateDialog';
import { TemplatePickerDialog } from '../templates/TemplatePickerDialog';

export const MAX_TARGET_GROUPS = 50; // BRD: a campaign can have a minimum of 1 and a maximum of 50 target groups

// Regions are optional (no regions = nationwide), so only Basics and Quotas make a target group 'complete'.
const SETUP_STEPS: { key: keyof TargetGroup; label: string; required: boolean }[] = [
  { key: 'basicSetting', label: 'Basics', required: true },
  { key: 'region', label: 'Regions', required: false },
  { key: 'profilingAnswers', label: 'Profiling', required: false },
  { key: 'quotaSettings', label: 'Quotas', required: true },
  { key: 'excludedProjects', label: 'Exclusions', required: false },
];

/** First required step that still has no saved data (falls back to the first step when everything is done). */
const nextStep = (tg: TargetGroup) => (!tg.basicSetting ? 'basics' : !tg.quotaSettings ? 'quotas' : 'basics');

// A saved part is an object (basics/quotas/region) or a non-empty list (profiling answers); [] is "not set".
const hasData = (v: unknown) => (Array.isArray(v) ? v.length > 0 : Boolean(v));

const yes = (v: unknown) => String(v).toLowerCase() === 'yes';

function SettingsList({ project }: { project: Project }) {
  const s = project.projectSettings || {};
  const ref = project.projectReference || {};
  const contact = [ref.contactName, ref.phone, ref.email].filter(Boolean).join(' · ');
  const devices = [yes(s.mobiles) && 'Mobile', yes(s.desktops) && 'Desktop', yes(s.tablets) && 'Tablet'].filter(Boolean);
  const rows: [string, string][] = [
    ['Devices', devices.join(', ') || '—'],
    ['Requires webcam', yes(s.requiresWebcam) ? 'Yes' : 'No'],
    ['Restrict region', yes(s.restrictRegion) ? 'Yes' : 'No'],
    ['Collects PII', yes(project.collectsPII) || yes(s.collectPersonalInfo) ? 'Yes' : 'No'],
    // The backend defaults this to "yes", so a campaign without the flag prevents duplicates.
    ['Prevent duplicates', s.preventDuplicates === undefined || yes(s.preventDuplicates) ? 'Yes' : 'No'],
    ['Starts', formatDate(project.startDate)],
    ['Ends', formatDate(project.endDate)],
    ['Contact', contact || '—'],
  ];
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
      {rows.map(([k, v]) => (
        <div key={k}><dt className="text-xs font-semibold text-muted">{k}</dt><dd className="mt-0.5 font-medium text-ink">{v}</dd></div>
      ))}
    </dl>
  );
}

export function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { notify } = useToast();
  const { data: project, error, loading, reload } = useAsyncData<Project>((signal) => getProject(projectId, { signal }), [projectId]);
  // Launching (or reopening) is gated by the server's readiness check; an active campaign has nothing to launch.
  const status = String((project && project.projectStatus) || '').toLowerCase();
  const readiness = useAsyncData<ProjectReadiness | null>((signal) => getProjectReadiness(projectId, { signal }), [projectId], { enabled: Boolean(project) && status !== 'active' });
  const refresh = async () => { await Promise.all([reload(), readiness.reload()]); };
  const [tab, setTab] = useState<'overview' | 'respondents'>('overview'); // the Respondents list loads only when its tab is opened
  const [adding, setAdding] = useState(false);
  const addingRef = useRef(false);
  const [editing, setEditing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false); // "Start from template"
  const [templateFor, setTemplateFor] = useState<TargetGroup | null>(null); // "Save as template"

  const targetGroups = (project && project.targetGroups) || [];
  const atLimit = targetGroups.length >= MAX_TARGET_GROUPS;

  const addTargetGroup = async () => {
    if (!project || addingRef.current || atLimit) return;
    addingRef.current = true;
    setAdding(true);
    try {
      const tg = await createTargetGroup(project.id, { name: `Target group ${targetGroups.length + 1}` });
      await ensureTargetGroupLinked(project.id, tg.id);
      navigate(paths.targetGroup(project.id, tg.id, 'basics'));
    } catch (err) {
      notify(errorMessage(err), { tone: 'danger' });
    } finally {
      addingRef.current = false;
      setAdding(false);
    }
  };

  if (loading && !project) return <div className="mx-auto max-w-6xl space-y-4" aria-busy="true"><Skeleton className="h-8 w-72" /><Skeleton className="h-32 w-full" /><Skeleton className="h-48 w-full" /></div>;
  if (error) return <Card className="mx-auto max-w-6xl"><ErrorState title="Could not load this campaign" message={errorMessage(error)} onRetry={reload} /></Card>;
  if (!project) {
    return (
      <Card className="mx-auto max-w-6xl">
        <EmptyState title="Campaign not found" description="It may have been deleted, or the link is wrong." action={<Link to={paths.projects()} className="text-sm font-semibold text-brand-700 hover:underline">Back to campaigns</Link>} />
      </Card>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <nav aria-label="Breadcrumb" className="text-xs text-muted">
        <Link to={paths.projects()} className="hover:underline">Campaigns</Link> <span aria-hidden="true">›</span> <span className="text-ink">{project.name}</span>
      </nav>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-bold tracking-tight text-ink">{project.name} <StatusBadge status={project.projectStatus} /></h1>
          <p className="mt-1 text-sm text-muted">
            {project.projectId ? `#${project.projectId} · ` : ''}
            {(project.projectCategoryId || []).map((c) => c.name).filter(Boolean).join(', ') || 'No category'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CampaignStatusControl project={project} launchReady={Boolean(readiness.data && readiness.data.ready)} launchHint={readiness.data ? 'Fix the items in "Ready to launch?" first' : 'Checking whether the campaign is ready to launch'} onChanged={refresh} />
          <Button variant="secondary" onClick={() => setPickerOpen(true)} disabled={atLimit} title={atLimit ? `A campaign can have at most ${MAX_TARGET_GROUPS} target groups` : undefined}>From template…</Button>
          <Button onClick={addTargetGroup} loading={adding} disabled={atLimit} title={atLimit ? `A campaign can have at most ${MAX_TARGET_GROUPS} target groups` : undefined}>＋ Add target group</Button>
        </div>
      </div>

      <Tabs label="Campaign sections" tabs={[{ key: 'overview', label: 'Overview' }, { key: 'respondents', label: 'Respondents' }]} value={tab} onChange={(k) => setTab(k as 'overview' | 'respondents')} />

      {tab === 'respondents' && <RespondentsPanel project={project} />}

      {tab === 'overview' && (
        <>
      <Card>
        <CardBody>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-semibold text-ink">Details</h2>
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>Edit details</Button>
          </div>
          <SettingsList project={project} />
        </CardBody>
      </Card>

      {status !== 'active' && <LaunchChecklist readiness={readiness} />}
      <CampaignProgressCard key={`p-${project.id}`} project={project} />
      <PricingCard key={`c-${project.id}`} project={project} />
      <EntryLinkCard project={project} />
      <RedirectLinksCard key={project.id} project={project} />
        </>
      )}
      <TemplatePickerDialog open={pickerOpen} project={project} onClose={() => setPickerOpen(false)} onOpenTargetGroup={(tgId, { fromTemplate }) => navigate(paths.targetGroup(project.id, tgId, 'basics'), { state: { fromTemplate } })} />
      <SaveAsTemplateDialog open={templateFor !== null} targetGroup={templateFor} onClose={() => setTemplateFor(null)} />
      <CampaignDetailsDialog key={`d-${project.id}`} project={project} open={editing} onClose={() => setEditing(false)} onSaved={refresh} />

      {tab === 'overview' && (
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4">
          <h2 className="text-base font-semibold text-ink">Target groups <span className="font-normal text-muted">· {targetGroups.length} of {MAX_TARGET_GROUPS}</span></h2>
        </div>
        {targetGroups.length === 0 ? (
          <EmptyState title="No target groups yet" description="Add a target group to define who you want to reach." action={<Button size="sm" onClick={addTargetGroup} loading={adding}>Add target group</Button>} />
        ) : (
          <Table caption="Target groups">
            <thead><tr><Th>Name</Th><Th>Setup</Th><Th>Wanted completes</Th><Th><span className="sr-only">Actions</span></Th></tr></thead>
            <tbody>
              {targetGroups.map((tg, i) => {
                const complete = SETUP_STEPS.filter((s) => s.required).every((s) => hasData(tg[s.key]));
                return (
                  <tr key={tg.id} className="hover:bg-canvas">
                    <Td className="font-semibold">{tg.name || `Target group ${i + 1}`}</Td>
                    <Td>
                      <div className="flex flex-wrap gap-1.5">
                        {SETUP_STEPS.map((s) => <Badge key={s.key} tone={hasData(tg[s.key]) ? 'ok' : 'neutral'}>{s.label}</Badge>)}
                      </div>
                    </Td>
                    <Td className="text-muted">{tg.basicSetting && tg.basicSetting.noOfWantedCompletes != null ? tg.basicSetting.noOfWantedCompletes : '—'}</Td>
                    <Td className="text-right">
                      <span className="inline-flex items-center gap-3">
                        {tg.basicSetting && <button type="button" aria-label={`Save ${tg.name || `target group ${i + 1}`} as template`} onClick={() => setTemplateFor(tg)} className="text-sm font-semibold text-muted hover:text-ink hover:underline">Save as template</button>}
                        <Link to={paths.targetGroup(project.id, tg.id, nextStep(tg))} className="text-sm font-semibold text-brand-700 hover:underline">
                          {complete ? 'Edit' : 'Continue setup'}
                        </Link>
                      </span>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
      )}
    </div>
  );
}
